import React from "react";
import { Link } from "react-router-dom";
import { FileText, Users, Home, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * TermsHub — landing page at bare `/terms` route. Sends visitors to either
 * the guest booking terms or the host agreement based on their role. The
 * /terms/:kind pages carry the actual legal text (admin-editable via the
 * Terms tab in AdminLayout).
 */
export default function TermsHub() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8" data-testid="terms-hub">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
        <FileText className="h-3.5 w-3.5" /> Terms of Service
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
        EasyStay Terms of Service
      </h1>
      <p className="mt-3 text-slate-600">
        EasyStay is Zambia&apos;s marketplace for hotels, lodges and guest houses — stays from 2 hours to full nights, mobile money or card, digital or physical keys. Our Terms are split by role so you only read what applies to you.
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Link
          to="/terms/guest"
          data-testid="terms-hub-guest-card"
          className="group rounded-3xl bg-white p-6 card-shadow transition hover:-translate-y-0.5 hover:shadow-lg"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-copper/10 text-copper">
            <Users className="h-5 w-5" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Guest Terms</h2>
          <p className="mt-2 text-sm text-slate-600">
            For anyone booking a stay on EasyStay — booking rules, cancellation, refunds, key delivery, mobile-money payments, and how disputes are handled.
          </p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-copper group-hover:gap-2">
            Read guest terms <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </span>
        </Link>

        <Link
          to="/terms/host"
          data-testid="terms-hub-host-card"
          className="group rounded-3xl bg-white p-6 card-shadow transition hover:-translate-y-0.5 hover:shadow-lg"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-forest/10 text-forest">
            <Home className="h-5 w-5" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Host Agreement</h2>
          <p className="mt-2 text-sm text-slate-600">
            For property owners listing hotels, lodges or guest houses — commission, payout schedule, listing standards, cancellations, and the KYC verification process.
          </p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-forest group-hover:gap-2">
            Read host agreement <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </span>
        </Link>
      </div>

      <div className="mt-10 rounded-2xl bg-slate-50 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Also useful</div>
            <p className="mt-1 text-sm text-slate-600">
              Read our{" "}
              <Link to="/privacy" className="text-copper hover:underline">Privacy Policy</Link>{" "}
              for how we handle your data, or head to our{" "}
              <Link to="/trust-safety" className="text-copper hover:underline">Trust & Safety</Link>{" "}
              page for verification and safe-stay guidelines. Questions? Reach us at{" "}
              <Link to="/contact" className="text-copper hover:underline">Contact</Link>.
            </p>
          </div>
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        These Terms are governed by the laws of Zambia. Last updated by EasyStay Zambia.
      </p>
    </div>
  );
}
